import type {
  CreateClubTableRequest,
  CreateClubTableResponse,
  HGCreateClubTableReply,
  TableParams
} from '@ps/web-shared-api-types/v1-preview/SwHomeGames';
import { useIsMounted } from '@ps/web-shared-components';
import type { FormApi } from 'final-form';
import { isEmpty } from 'lodash';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { lastValueFrom } from 'rxjs';

import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import { ENDPOINTS, handleServerResponse } from '@swf/poker';

import { HGOnboarding, ModalRoutes } from '../../../types/common';
import type { CreateModalLocationState } from '../../../types/common';
import type { FormValues } from '../utils/common';
import { getMatchingGameObject } from '../utils/common';

import { TXTSRV_SorryX_we_are_experiencing_tem_ELL } from './useCreateClubTable.messages.json';

interface State {
  error?: string | null;
  message?: string | null;
  data: HGCreateClubTableReply;
}

interface LocationState extends CreateModalLocationState {
  clubId: number;
  widgetId?: string;
}

const initialResponse: State = { error: null, message: null, data: {} };

const useCreateClubTable = (data: TableParams[], setError: (error: string) => void) => {
  const request = useRequest();
  const { formatMessage } = useIntl();
  const [response, setResponse] = useState(initialResponse);
  const [loading, setLoading] = useState(false);
  const { modal } = useDispatch<Dispatch>();
  const isMounted = useIsMounted();

  const {
    state: { clubId, widgetId, widgetSelect, widgetDispatch }
  } = useLocation<LocationState>();

  const tables = useSelector(widgetSelect.tables);
  const tourns = useSelector(widgetSelect.tournaments);
  const noGames = isEmpty(tables) && isEmpty(tourns);

  const handleSubmit = useCallback(
    async (values: FormValues, form: FormApi<FormValues, Partial<FormValues>>) => {
      const tableParams = getMatchingGameObject(form.getState().values, data);

      if (!tableParams) return;

      setLoading(true);

      const payload: CreateClubTableRequest = {
        createClubTable: {
          clubId,
          tableParams: {
            ...tableParams,
            tableName: values.tableName
          },
          videoChatEnabled: form.getState().values.videoChatEnabled
        }
      };

      try {
        const { body } = await lastValueFrom(
          request<CreateClubTableResponse>({
            service: ENDPOINTS.clubCreateTable,
            method: 'PUT',
            body: payload,
            http: true
          })
        );

        const { error, data, message } = handleServerResponse<
          CreateClubTableResponse,
          HGCreateClubTableReply
        >({
          response: body,
          accessor: 'createClubTableReply'
        });

        setResponse({ error, data, message });

        if (!error) {
          if (noGames) {
            widgetDispatch.setLocalStorageItem({
              onboarding: {
                [HGOnboarding.CreatedGame]: true
              }
            });
          }
          modal.replace([{ path: ModalRoutes.createCashGameSuccess, locationState: { widgetId } }]);

          window.starsweb?.homegames?.refresh?.();
        } else {
          setError(error);
        }
      } catch (error) {
        if (isMounted()) {
          setError(formatMessage(TXTSRV_SorryX_we_are_experiencing_tem_ELL));
          setResponse({
            data: {},
            message: ''
          });
        }
      } finally {
        if (isMounted()) {
          setLoading(false);
        }
      }
    },
    [
      data,
      clubId,
      request,
      noGames,
      modal,
      widgetId,
      widgetDispatch,
      setError,
      isMounted,
      formatMessage
    ]
  );

  return { handleSubmit, response, loading };
};

export default useCreateClubTable;
